// ComplexHeaderCard — the development itself, above the flats in it. Owner 2026-09-10:
// "complex search must show that complex's details on first place and then apartments below".
//
// WHEN IT RENDERS: exactly ONE complex identity chip is committed (advFilters.complexIds has one
// id). Two or more is a family ("all Archi · 34") and there is no single subject to describe;
// zero is no question. The caller decides that — this component only draws a row it is given.
//
// WHAT IT DOES NOT SHOW, on purpose:
//   • No count. The feed directly beneath already states "3 ლიდი" / the browse total, and the
//     card-face rule is that the same fact never renders twice. A second number here would also
//     be a second DEFINITION of "how many are in this complex" (B273), unless it came from the
//     very same query — and then it is the number already on screen.
//   • No distance, no radius. COMPLEX_MATCHING.md: distance settles nothing in either direction.
//   • No `details` blob. The server forwards five named, scalar keys and nothing else; a
//     developer's pasted unit dump must never reach a screen.
//
// EMPTY FIELDS ARE OMITTED, NOT DASHED. Measured 2026-09-10 on 352 rows: name 352, name_ka 317,
// developer 265, district 313, pin 343 — but status 77, floors 56, blocks 22. A grid of "—" for
// most developments would say "we know little", which is true and useless; a short row of what
// IS known reads as a fact card. The name line is the only thing guaranteed.
//
// THREE ACTIONS, all of which already exist elsewhere and are only re-doored here:
//   ✕ removes the chip (the SAME removeChip path the chip bar uses — one removal definition),
//   "all N's projects" commits the developer family (acceptComplexFamily, the suggestion row's
//   verb), and "show on map" switches the entries view — the feed is already filtered, so the
//   map shows this complex's flats without a new query.
import React from 'react'
import { Icon } from '../Icon.jsx'

// Status values seen on `complex.status`: planned | under_construction | partial | completed,
// plus `built` from an older seed. Vocabulary keys already exist for every one (desk_cplx_status_*);
// an unknown value renders nothing rather than a raw key (the 0427 lesson: `t()` of a missing
// key is the key itself, in English, on a Georgian screen).
const STATUS_KEYS = new Set(['planned', 'under_construction', 'partial', 'completed', 'built'])

// `complex.images`: [{ url, width, height }] — the developer's OWN gallery, imported from the
// official page into complex_image (migration 1033, scripts/import-complex-gallery.mjs).
// Owner 2026-09-10, correcting the first cut (which drew the linked flats' cover photos):
// "not listings images but complexes own image gallery imported from official page." A
// development is not the sum of the flats advertised in it. Empty = no gallery imported
// (draw nothing; 135 of 352 rows have an official page, the rest cannot have one yet).
// A tap opens the full image in a new tab — the developer's own file, hotlinked.
export default function ComplexHeaderCard({ complex, lang = 'ka', districtName, t, onRemove, onDeveloper, onShowOnMap }) {
  if (!complex) return null
  const tr = t || (k => k)
  const ka = complex.name_ka || null
  const en = complex.name || null
  // Interface language first, the other name as the secondary line — the SAME rule the chip
  // label follows (acceptComplex), so the card and the chip never disagree on what it is called.
  const primary = lang === 'ka' ? (ka || en) : (en || ka)
  const secondary = lang === 'ka' ? (ka && en && en !== ka ? en : null) : (en && ka && ka !== en ? ka : null)
  const d = complex.details || {}
  const district = complex.district_code && districtName ? districtName(complex.district_code) : null
  const floors = complex.floors_min != null && complex.floors_max != null
    ? (complex.floors_min === complex.floors_max ? String(complex.floors_max) : `${complex.floors_min}–${complex.floors_max}`)
    : (complex.floors_max != null ? String(complex.floors_max) : null)
  const status = complex.status && STATUS_KEYS.has(complex.status) ? tr(`desk_cplx_status_${complex.status}`) : null
  const eta = d.eta || d.completion || null

  const facts = [
    district && [tr('desk_cxhead_district'), district],
    status && [tr('desk_cxhead_status'), status],
    eta && [tr('desk_cxhead_eta'), eta],
    complex.blocks != null && [tr('desk_cxhead_blocks'), String(complex.blocks)],
    floors && [tr('desk_cxhead_floors'), floors],
    d.address_ka && [tr('desk_cxhead_address'), d.address_ka],
  ].filter(Boolean)

  let siteHost = null
  if (d.source_url) { try { siteHost = new URL(d.source_url).hostname.replace(/^www\./, '') } catch { siteHost = null } }

  return (
    <div data-testid="complex-header" className="desk-card bg-card border border-border rounded-xl px-4 py-3 mb-3">
      {/* flex-wrap: on a phone the action group drops under the facts instead of clipping —
          measured in the harness at 682 px, where the site link, map and ✕ were off-screen. */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="shrink-0 mt-0.5 text-muted-foreground"><Icon name="building" /></div>
        <div className="min-w-0 flex-1 basis-[16rem]">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-base font-semibold leading-tight truncate" title={primary}>{primary}</h3>
            {secondary && <span className="text-sm text-muted-foreground truncate" title={secondary}>{secondary}</span>}
          </div>
          {(complex.developer || d.builder) && (
            <div className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
              <span>{tr('desk_cxhead_developer')}:</span>
              <span className="text-foreground">{complex.developer || d.builder}</span>
              {complex.developer && onDeveloper && (
                <button type="button" className="ml-1 text-xs underline decoration-dotted hover:text-foreground"
                  onClick={() => onDeveloper(complex.developer)}>
                  {tr('desk_cxhead_all_projects')}
                </button>
              )}
            </div>
          )}
          {facts.length > 0 && (
            <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              {facts.map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-1.5 min-w-0">
                  <dt className="text-muted-foreground shrink-0">{k}</dt>
                  <dd className="truncate" title={v}>{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {complex.aliases?.length > 0 && (
            // Aliases are how Georgian adverts actually write the name (ჰუალინგი for "Hualing").
            // Showing them tells the agent which spellings the linker recognises — and which it
            // does not, which is the correction they can make.
            <div className="mt-1.5 text-xs text-muted-foreground truncate" title={complex.aliases.join(' · ')}>
              {tr('desk_cxhead_aliases')}: {complex.aliases.slice(0, 6).join(' · ')}{complex.aliases.length > 6 ? ' …' : ''}
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1 ml-auto">
          {siteHost && (
            <a href={d.source_url} target="_blank" rel="noopener noreferrer"
              className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent" title={d.source_url}>
              {siteHost} ↗
            </a>
          )}
          {complex.lat != null && complex.lng != null && onShowOnMap && (
            <button type="button" onClick={onShowOnMap}
              className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent inline-flex items-center gap-1">
              <Icon name="map" /> {tr('desk_cxhead_show_map')}
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={onRemove} aria-label={tr('desk_cxhead_remove')} title={tr('desk_cxhead_remove')}
              className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground">
              <Icon name="x" />
            </button>
          )}
        </div>
      </div>
      {Array.isArray(complex.images) && complex.images.length > 0 && (
        // Horizontal, scrollable, never wrapping: 96 px tiles, one row on a desktop and a
        // swipe on a phone. Same object-cover framing as ListingGallery's tiles.
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="complex-thumbs">
          {complex.images.map(im => (
            <a key={im.url} href={im.url} target="_blank" rel="noopener noreferrer"
              className="relative shrink-0 w-24 h-16 rounded-md overflow-hidden bg-muted border border-border hover:ring-2 hover:ring-ring focus:outline-none focus:ring-2 focus:ring-ring"
              title={tr('desk_cxhead_open_image')}>
              <img src={im.url} alt="" loading="lazy" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
